import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SafeHtml } from '@angular/platform-browser';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
	ActionDialogComponent,
	ActionDialogData,
	ActionDialogResult,
	ActionDialogState,
} from '../components/action-dialog/action-dialog.component';
import {
	ActionProgressDialogComponent,
	ActionProgressDialogData,
	ActionProgressDialogResult,
} from '../components/action-progress-dialog/action-progress-dialog.component';
import { InfoDialogComponent, InfoDialogData } from '../components/info-dialog/info-dialog.component';
import {
	SpecifyReasonDialogComponent,
	SpecifyReasonDialogData,
	SpecifyReasonResult,
} from '../components/specify-reason-dialog/specify-reason-dialog.component';
import { DialogLevel } from '../enums/dialog-level.enum';
import { DialogSize } from '../enums/dialog-size.enum';

export interface ConfirmDialogData {
	body: SafeHtml;
	submitButton: string;
	title: string;

	cancelButton?: string;
	level?: DialogLevel;
	size?: DialogSize;
}

@Injectable()
export class StandardDialogService {
	private matDialog = inject(MatDialog);

	showActionDialog<T>(data: ActionDialogData<T>): Observable<ActionDialogResult<T>> {
		return this.matDialog
			.open<ActionDialogComponent<T>, ActionDialogData<T>, ActionDialogResult<T>>(ActionDialogComponent, { data })
			.afterClosed();
	}

	/**
	 * Show an action-dialog with progress-bar for e.g. multiple endpoint-requests defined in data.requests as array of observables
	 * The behaviour and config is nearly the same as in normal action-dialog.
	 *
	 * @param data
	 */
	showActionProgressDialog<T>(data: ActionProgressDialogData<T>): Observable<ActionProgressDialogResult<T>> {
		return this.matDialog
			.open<ActionProgressDialogComponent<T>, ActionProgressDialogData<T>, ActionProgressDialogResult<T>>(ActionProgressDialogComponent, {
				data,
			})
			.afterClosed();
	}

	showInfoDialog(data: InfoDialogData): Observable<void> {
		return this.matDialog.open<InfoDialogComponent, InfoDialogData>(InfoDialogComponent, { data }).afterClosed();
	}

	showConfirmationDialog(confirmDialogData: ConfirmDialogData): Observable<boolean> {
		const data: ActionDialogData<void> = { ...confirmDialogData, action$: null };
		return this.matDialog
			.open<ActionDialogComponent<void>, ActionDialogData<void>, ActionDialogResult<void>>(ActionDialogComponent, {
				data,
				panelClass: 'confirmation-dialog',
			})
			.afterClosed()
			.pipe(map((result: ActionDialogResult<void>) => result.state === ActionDialogState.SUCCESS));
	}

	showSpecifyReasonDialog<T>(data: SpecifyReasonDialogData<T>): Observable<SpecifyReasonResult<T>> {
		return this.matDialog
			.open<SpecifyReasonDialogComponent<T>, SpecifyReasonDialogData<T>, SpecifyReasonResult<T>>(SpecifyReasonDialogComponent, {
				data,
			})
			.afterClosed();
	}
}
